import axios from "axios"
import { DateTime, FixedOffsetZone } from "luxon"
import { action, computed, makeAutoObservable, makeObservable, observable, runInAction } from "mobx"

// 1000 ms -> 60s -> 10m
const pollingPeriod = 1000 * 60 * 10

// if another service is polling we use this to avoid duplicating the requests
// 1000 ms -> 60s -> 11m
const delayedPollingPeriod = 1000 * 60 * 11

// if we're loading the service then use a quick 1s refresh
// if we don't do this then the history service instantiates
// and requests a value from us before we're done
const quickRefreshDelay = 1000

const separator = "."


type StorageEntry<T> = {

	sourceId: number
	value: T

}

const windowId = window.performance?.now() ?? 0
export { windowId as WindowId }

class SessionData {

	private _authToken: FormsJwtAccessToken

	// current time in nanoseconds, used to register our id for other tabs
	// https://developer.mozilla.org/en-US/docs/Web/API/Performance/now


	get authToken() {
		if (!this._authToken)
			this._authToken = new FormsJwtAccessToken()

		return this._authToken
	}

	set authToken(value: FormsJwtAccessToken) {
		this._authToken = value
	}

	/**
	 *
	 */
	constructor() {
		makeObservable<SessionData, "_authToken">(this, {
			_authToken: observable
		});

		this._authToken = new FormsJwtAccessToken()
	}
}

type Token = {
	sub: number
	jti: string
	apid: string

	nbf: number
	exp: number
	iat: number
	iss: string
	aud: string
}

type IdToken = Token & {
	orgId: number
	orgname: string
	anonymous: boolean
	given_name: string
	family_name: string
	email: string
}

type AccessToken = Token & {
	anonymous: boolean
	token_type: 'access'
}

type RefreshToken = Token & {
	token_type: 'refresh'
}

class EnhancedTokenService<T extends Token> {

	private _storedToken: string | null = null

	public get tokenPayload(): Readonly<T> | undefined {
		if (this._storedToken === null)
			return undefined
		return this.parseToken(this._storedToken)
	}

	public get raw(): string | undefined {
		const storedToken = this._storedToken;
		if (storedToken === null)
			return undefined;

		return storedToken;
	}

	public get tokenName(): string {
		return this._tokenName;
	}

	constructor(private _tokenName: string) {
		makeAutoObservable(this, {})

		runInAction(() => { this._storedToken = localStorage.getItem(this._tokenName); })
	}

	setToken(token: string | null) {
		if (token === null) {
			window.localStorage.removeItem(this._tokenName);
			runInAction(() => { this._storedToken = null })
			return
		}

		this.parseToken(token);
		window.localStorage.setItem(this._tokenName, token);
		runInAction(() => { this._storedToken = token })

		startRefreshTask()
	}

	removeToken() {
		window.localStorage.removeItem(this._tokenName);
		runInAction(() => { this._storedToken = null })
	}

	private parseToken(token: string): T {
		const tokenParts = token.split(separator);

		if (!tokenParts[1])
			throw new Error('attempting to set bad token')

		const tokenBody = JSON.parse(window.atob(tokenParts[1])) as T
		return tokenBody;
	}

	isInvalidOrExpired(): boolean {
		const payload = this.tokenPayload;
		if (!payload)
			return true
		const dateTime = DateTime.fromSeconds(payload.exp, {
			zone: FixedOffsetZone.utcInstance
		})

		const expired = dateTime.diffNow().milliseconds < 0;
		if (expired)
			console.log('token detected expired')

		return expired
	}
}

class SessionService {
	idToken = new EnhancedTokenService<IdToken>('id_token')
	accessToken = new EnhancedTokenService<AccessToken>('access_token')
	refreshToken = new EnhancedTokenService<RefreshToken>('refresh_token')

	constructor() {
		makeAutoObservable(this)
	}

	public logout() {
		this.idToken.removeToken();
		this.accessToken.removeToken();
		this.refreshToken.removeToken();
	}
}

export const sessionService = new SessionService()

export async function refreshTokens(refreshToken: string) {
	if (!navigator.onLine)
		return;

	type Response = {
		idToken: string
		accessToken: string
		refreshToken: string
	}

	const result = await axios.post<Response>(`/api/v1/authentication/refresh`, { refreshToken: refreshToken}, {
		headers: {
			'content-type': 'application/json'
		}
	})

	sessionService.idToken.setToken(result.data.idToken)
	sessionService.accessToken.setToken(result.data.accessToken)
	sessionService.refreshToken.setToken(result.data.refreshToken)

	return result
}

const refreshTaskIdContainer = {
	id: 0
}

function clearRefreshTask() {
	window.clearTimeout(refreshTaskIdContainer.id)
}

function startRefreshTask(pollingPeriod?: number): void {
	window.clearTimeout(refreshTaskIdContainer.id)
	console.log('refresh timeout cleared')
	refreshTaskIdContainer.id = 0

	if (!sessionService.accessToken) {
		refreshTaskIdContainer.id = 0
		return
	}

	const tokenOwner = localStorage.getItem('token_owner');
	let tokenOwnerId = parseInt(tokenOwner ?? "")

	if (Number.isNaN(tokenOwnerId)) {
		tokenOwnerId = windowId
		localStorage.setItem('token_owner', windowId.toString())
	}

	const refreshDelay = tokenOwnerId === windowId ? pollingPeriod : delayedPollingPeriod;

	refreshTaskIdContainer.id = window.setTimeout(() => {
		const rawToken = sessionService.accessToken.raw;
		if (rawToken === undefined) {
			clearInterval(refreshTaskIdContainer.id)
			return
		}
		refreshTokens(rawToken)
	}, refreshDelay)
}

export abstract class TokenService<T extends JwtBody> {
	protected internalPollingPeriod = pollingPeriod
	private _refreshTaskId = 0

	protected _rawToken = "";

	constructor() {
		makeObservable<TokenService<T>, "_rawToken">(this, {
			_rawToken: observable,
			notBefore: computed,
			expirationDate: computed,
			issuedAt: computed,
			issuer: computed,
			audience: computed,
			expired: computed,
			setToken: action
		});
	}

	public get notBefore(): Date {
		return this.convertDate(this.body?.nbf ?? 0)
	}

	public get expirationDate(): Date {
		return this.convertDate(this.body?.exp ?? 0)
	}

	public get issuedAt(): Date {
		return this.convertDate(this.body?.iat ?? 0)
	}

	public get issuer(): string {
		return this.body?.iss ?? ""
	}

	public get audience(): string {
		return this.body?.iss ?? ""
	}

	public get expired(): boolean {
		return this.isExpired(this.body?.exp ?? 0)
	}

	public setToken(tokenValue: string): void {
		this._rawToken = tokenValue

		this.restartRefreshTask(() => this.triggerTokenUpdate())
	}

	protected get refreshTaskId(): number {
		console.log('refresh id fetched', this._refreshTaskId)
		return this._refreshTaskId
	}

	protected set refreshTaskId(id: number) {
		console.log('refresh id set', this._refreshTaskId)
		this._refreshTaskId = id
	}

	public setPollingPeriod(pollingPeriod: number): void {
		this.internalPollingPeriod = pollingPeriod
	}


	public restartRefreshTask(event: () => unknown, pollingPeriod?: number): void {
		window.clearTimeout(this.refreshTaskId)
		console.log('refresh timeout cleared')
		this._refreshTaskId = 0

		if (!this.rawToken) {
			this._refreshTaskId = 0
			return
		}

		this._refreshTaskId = window.setTimeout(() => event(), pollingPeriod ?? this.internalPollingPeriod)
	}

	public abstract get rawToken(): string
	public abstract set rawToken(token: string)

	public abstract get body(): T | null

	public abstract logout(): void

	protected getToken<T>(tokenName: string):
		StorageEntry<T> | null {
		const entry = localStorage.getItem(tokenName)
		return entry
			? JSON.parse(entry) as StorageEntry<T>
			: null
	}

	protected isExpired(expirationDate: number): boolean {
		// JWT tokens are in seconds, * 1000 to get JS Date
		return this.convertDate(expirationDate) <= new Date()
	}

	protected convertDate(seconds: number): Date {
		return new Date(seconds * 1000)
	}

	protected abstract triggerTokenUpdate(): void
}

export class FormsJwtAccessToken extends TokenService<FormsJwtBody> {

	static tokenName = "FJWT"

	private readonly tokenIndicator = "Bearer "

	public get rawToken(): string {
		return this._rawToken
	}

	public set rawToken(value: string) {
		this._rawToken = value

		console.log('refresh token set', value)

		this.restartRefreshTask(() => this.triggerTokenUpdate())

		// there will have been an error if we have a missing value
		if (!value) {
			localStorage.removeItem(FormsJwtAccessToken.tokenName)
			return
		}

		// persist our session in local storage
		const sharedToken: StorageEntry<string> = {
			'sourceId': windowId,
			'value': value
		}
		localStorage.setItem(FormsJwtAccessToken.tokenName, JSON.stringify(sharedToken))
	}


	public get body(): FormsJwtBody | null {

		if (this.rawToken === null || this.rawToken === undefined)
			return null

		const tokenParts = this.isolatedToken.split(separator)

		if (tokenParts[1] === undefined)
			return null

		return JSON.parse(window.atob(tokenParts[1])) as FormsJwtBody
	}

	private get isolatedToken(): string {
		if (!this._rawToken)
			return ""
		return this.rawToken.substring(this.tokenIndicator.length)
	}


	public get id(): number | undefined {
		return this.body?.sub ?? undefined
	}

	public get firstName(): string | null {
		return this.body?.given_name ?? null
	}

	public get lastName(): string | null {
		return this.body?.family_name ?? null
	}

	public get email(): string | null {
		return this.body?.email ?? null
	}

	public get version(): string | null {
		return this.body?.version ?? null
	}

	public get groups(): string[] | null {
		const result = this.body?.group ?? []
		return Array.isArray(result) ? result : [result]
	}

	public get roles(): string[] | null {
		const result = this.body?.role ?? []
		return Array.isArray(result) ? result : [result]
	}

	public get orgName(): string | null {
		return this.body?.orgname ?? null
	}

	public get subDomain(): string | null {
		return this.body?.subdomain ?? null
	}

	constructor() {
		super()

		makeObservable<FormsJwtAccessToken, "isolatedToken">(this, {
			rawToken: computed,
			body: computed,
			isolatedToken: computed,
			id: computed,
			firstName: computed,
			lastName: computed,
			email: computed
		});

		this.fetchStoredToken()
	}

	private fetchStoredToken(): void {
		// attempt to get the token from storage
		const storedToken = this.getToken<string>(FormsJwtAccessToken.tokenName)
		if (!storedToken)
			return

		// if we have a token then we can trigger a token update
		this.rawToken = storedToken.value

		window.setTimeout(() => this.triggerTokenUpdate(), quickRefreshDelay)

		this.restartRefreshTask(() => this.triggerTokenUpdate())
	}

	public logout(): void {
		localStorage.removeItem(FormsJwtAccessToken.tokenName)
		this.rawToken = ""
	}

	protected async triggerTokenUpdate(): Promise<void> {

		if (sessionService.refreshToken.raw) {
			refreshTokens(sessionService.refreshToken.raw).then(v => {
				console.log('tokens updated')
			})
			console.log('fetching new authorization token')
		} else {
			console.log('no token to trigger refresh')
		}

	}
}

type JwtBody = {
	nbf: number
	exp: number
	iat: number
	iss: string
	aud: string
	sub: number
}

export type FormsJwtBody = JwtBody & {
	given_name: string
	family_name: string
	email: string
	// - new
	version: string
	group: string | string[]
	role: string | string[]
	orgname: string
	subdomain: string
}

const sessionDataInstance = new SessionData()
console.log('session data initialized')
// export default sessionDataInstance
export default sessionService;

